<template>
  <modal-fullscreen
    title="View Document"
    :visible="visible"
    :destroy-on-close="true"
    width="600px"
    data-testid="document-view-modal"
    @cancel="closeModal"
    v-if="visible"
  >
    <div class="document-actions" v-if="document && associate_id" data-testid="document-view-actions">
      <div class="flex flex-column gap-4">
        <h4 class="m-0">Actions</h4>
        <export-button v-if="document" :export-payload="exportPayload" :button-style="{ width: '100%' }" />
      </div>
    </div>

    <div v-if="loading || !document" class="loading-spin" style="height: 650px" data-testid="document-view-loading">
      <a-spin />
    </div>

    <div class="flex flex-column gap-3" v-if="document && !loading" data-testid="document-view-content">
      <h4 class="m-0">{{ document.name }}</h4>

      <a-descriptions :column="1" size="small" :colon="false" class="col-fixed p-0">
        <a-descriptions-item label="Type">
          <a-tag class="m-0" :color="getDocumentType(document.type).color">
            {{ getDocumentType(document.type).title }}
          </a-tag></a-descriptions-item
        ><a-descriptions-item label="Group">
          <a-tooltip v-if="document.group">
            <template slot="title">
              {{ document.group.description }}
            </template>
            <div class="flex flex-column gap-2">
              <a-tag class="m-0" :color="getGroupedColor(document.group.id)">
                {{ document.group.name }}
              </a-tag>
            </div>
          </a-tooltip>

          <span v-else>-</span></a-descriptions-item
        ><a-descriptions-item label="Required in Company">
          {{ document.required_in_project ? 'Yes' : 'No' }}</a-descriptions-item
        ><a-descriptions-item label="Public"> {{ document.is_public ? 'Yes' : 'No' }}</a-descriptions-item
        ><a-descriptions-item v-if="document.expiration_period" label="Expiration Period">
          {{ document.expiration_period / 24 / 60 }} days</a-descriptions-item
        ><a-descriptions-item v-if="document.update_period" label="Update Period">
          {{ formatDate(document.update_period) }} </a-descriptions-item
        ><a-descriptions-item label="Create Date"> {{ formatDate(document.create_date) }} </a-descriptions-item
        ><a-descriptions-item label="Update Date">
          {{ formatDate(document.current_version_create_date) }} </a-descriptions-item
        ><a-descriptions-item label="Current Version"> {{ document.current_version }}</a-descriptions-item
        ><a-descriptions-item v-if="document.scope.exclude_roles?.length" label="Target All Except Positions">
          {{ document.scope.exclude_roles.map(item => item.name ?? item).join(', ') }}</a-descriptions-item
        ><a-descriptions-item
          v-if="document.scope.exclude_legal_entities?.length"
          label="Target All Except Legal Entities"
        >
          {{ document.scope.exclude_legal_entities.map(item => item.name ?? item).join(', ') }}</a-descriptions-item
        ><a-descriptions-item v-if="document.scope.payment_types.length" label="Target Payment Types">
          {{ formatTargetPaymentTypes(document.scope.payment_types, document.scope.contractor_sub_types) }}</a-descriptions-item
        ><a-descriptions-item v-if="document.scope.roles.length" label="Target Positions">
          {{ document.scope.roles.map(item => item.name).join(', ') }}</a-descriptions-item
        ><a-descriptions-item v-if="document.scope.legal_entities.length" label="Target Legal Entities">
          {{ document.scope.legal_entities.map(item => item.name).join(', ') }}</a-descriptions-item
        >
      </a-descriptions>

      <template v-if="document.type === 'pdf'"> <pdf-viewer :file="document.body.file.url" /></template>
      <template v-else-if="document.type === 'text'">
        <p class="m-0" v-html="safeDocumentBodyItems(document.body.items)"></p>
      </template>
      <template v-else-if="document.type === 'video'">
        <video
          v-if="document.body.file.url"
          class="ant-col-24 mt-5"
          :style="{ height: '336px' }"
          :src="document.body.file.url"
          controls
        ></video>
        <p v-if="document.body.details" class="m-0">Details: {{ document.body.details }}</p>
      </template>
      <template v-else-if="document.type === 'survey'">
        <p v-if="document.body.survey.details" class="m-0">Details: {{ document.body.survey.details }}</p>
        <a-progress
          v-if="document.body.survey.associate_result"
          :status="
            countCompletionRate(document.body.survey.associate_result?.completion_rate) >=
            document.body.survey.completion_rate
              ? 'success'
              : 'exception'
          "
          :percent="countCompletionRate(document.body.survey.associate_result?.completion_rate)"
        />
        <div class="questions-list">
          <div v-for="(question, idx) in document.body.survey.questions" :key="question.id">
            <div class="flex gap-2 mb-1">
              <a-badge :number-style="{ backgroundColor: '#3b82f6' }" :count="idx + 1" />

              {{ question.name }}
            </div>

            <div class="answers-list">
              <div
                class="answer"
                :style="{
                  borderColor: answer.correct ? '#10b981' : '#ef4444'
                }"
                v-for="answer in question.answers"
                :key="answer.id"
              >
                <a-checkbox
                  v-if="document.body.survey.associate_result"
                  :checked="checkedAnswer(answer, document.body.survey.associate_result?.answers)"
                />

                <answer-correctness-icon :correct="answer.correct" />
                {{ answer.name }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-if="document.signature">
        <p>Associate Signature</p>
        <associate-signature key="associate_signature" :available-signature="document.signature" />
      </div>
    </div>

    <template slot="footer">
      <div class="flex justify-content-between" data-testid="document-view-footer">
        <a-button @click="closeModal" data-testid="document-view-close-button">Close</a-button>
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import PdfViewer from '@/components/entities/viewer/pdf-viewer.vue'
import ExportButton from '@/components/export-button.vue'
import Signature from '@/components/signature.vue'
import { Badge, Button, Checkbox, Descriptions, notification, Progress, Spin, Tag, Tooltip } from 'ant-design-vue'
import moment from 'moment-timezone'
import { joinHtmlItems } from '@/utils/safe-html'
import { countCompletionRate } from '@/utils/survey'
import { createNamespacedHelpers } from 'vuex'
import AnswerCorrectnessIcon from './answer-correctness-icon.vue'
import { formatTargetPaymentTypes } from '@/utils/contractor-sub-types'

const {
  mapActions: documentActions,
  mapState: documentState,
  mapGetters: documentGetters
} = createNamespacedHelpers('document')

export default {
  components: {
    'a-button': Button,
    'a-spin': Spin,
    'a-progress': Progress,
    'a-checkbox': Checkbox,
    'a-badge': Badge,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-tooltip': Tooltip,
    'a-tag': Tag,
    'export-button': ExportButton,
    'pdf-viewer': PdfViewer,
    'associate-signature': Signature,
    'answer-correctness-icon': AnswerCorrectnessIcon
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    document_id: {
      type: Number,
      default: undefined
    },
    associate_id: {
      type: Number,
      default: undefined
    },
    version: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      loading: false,

      colors: ['#D47AFF', '#33DFCF', '#10b981', '#21E29D', '#3b82f6', '#6C69FF', '#f59e0b']
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal && !this.associate_id) {
          this.fetchDocument()
        } else if (newVal && this.associate_id && this.version) {
          this.fetchSignedDocument()
        }
      },
      immediate: true
    }
  },
  computed: {
    ...documentGetters(['getDocumentType']),

    ...documentState({
      document: state => state.document
    }),

    exportPayload() {
      return this.associate_id
        ? {
            type: 'project_document_version',
            object_ids: [this.document.signed_version_id]
          }
        : {
            type: 'project_document_version',
            project_document_version_id: this.document.version_id
          }
    }
  },
  methods: {
    ...documentActions(['getProjectDocument', 'getSignedDocument']),
    formatTargetPaymentTypes,

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    safeDocumentBodyItems(items) {
      return joinHtmlItems(items)
    },

    formatDate(date) {
      return moment(date).format(this.$store.state.applicationState.dateFormat)
    },

    getGroupedColor(id) {
      const colorIndex = id % this.colors.length
      return this.colors[colorIndex]
    },

    checkedAnswer(answer, result) {
      return result.some(item => item.id === answer.id)
    },

    countCompletionRate,

    closeModal() {
      this.$emit('update:visible', false)
      this.$emit('update:document_id', undefined)
    },

    async fetchDocument() {
      try {
        this.loading = true

        const payload = {
          document_id: this.document_id,
          version_number: this.version
        }

        await this.getProjectDocument(payload)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    },

    async fetchSignedDocument() {
      try {
        this.loading = true

        const payload = {
          document_id: this.document_id,
          version: this.version,
          associate_id: this.associate_id
        }

        await this.getSignedDocument(payload)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.questions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 300px;
  overflow: auto;
  border: 1px solid var(--border-color-base);
  border-radius: 10px;
  padding: 16px;
}

.answers-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.answer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--border-color-base);
  border-radius: 10px;
}

.document-actions {
  position: absolute;
  right: -180px;
  background-color: var(--bg-component);
  width: 160px;
  height: fit-content;
  top: 0;
  border-radius: 12px;
  padding: 16px 20px;
}
</style>
